import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMounted } from '@/lib/use-mounted';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Phone, Mail, Clock, MapPin, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { SEO } from '@/components/SEO';
import { SITE_URL, SITE_NAME, PHONE } from '@/lib/seo';
import { trackEvent } from '@/lib/analytics';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const contactSchema = z.object({
  name: z.string().min(2, 'Full name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().min(7, 'Valid phone number is required'),
  message: z.string().min(10, 'Please describe how we can help (10+ characters)'),
});

type ContactValues = z.infer<typeof contactSchema>;

const HOURS = [
  { day: 'Monday – Friday', hours: '8:00 AM – 6:00 PM' },
  { day: 'Saturday',         hours: '9:00 AM – 2:00 PM' },
  { day: 'Sunday',           hours: 'Closed' },
];

export default function Contact() {
  const mounted = useMounted();
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: '', email: '', phone: '', message: '' },
  });

  const isLoading = form.formState.isSubmitting;

  async function onSubmit(values: ContactValues) {
    setSubmitError(null);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? 'Something went wrong. Please try again.');
      }
      setIsSuccess(true);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  return (
    <Layout>
      <SEO
        title="Contact Us — Truck Max Equipment Finance"
        description="Contact Truck Max Equipment Finance for commercial truck and equipment financing. Call +1-866-441-0513 or send us a message. 2-hour credit decisions, serving all of Florida."
        path="/contact"
        jsonLd={[
          {
            '@type': ['LocalBusiness', 'FinancialService'],
            '@id': `${SITE_URL}/#organization`,
            name: SITE_NAME,
            telephone: PHONE,
            email: 'info@truckmaxequipmentfinance.com',
            url: `${SITE_URL}/contact`,
            openingHoursSpecification: [
              { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '08:00', closes: '18:00' },
              { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '09:00', closes: '14:00' },
            ],
            areaServed: { '@type': 'State', name: 'Florida' },
          },
        ]}
      />

      {/* Hero */}
      <section className="bg-[#1a2f7e] pt-36 pb-20 text-white">
        <div className="container mx-auto px-4 lg:px-6">
          <motion.div key={mounted ? "hero-on" : "hero-off"} initial={mounted ? "hidden" : false} animate="visible" variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }}>
            <motion.p variants={fadeUp} className="text-[#cc2020] font-bold uppercase tracking-widest text-sm mb-3">
              Get In Touch
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-3xl md:text-5xl font-extrabold tracking-tight mb-5">
              Contact Truck Max Finance
            </motion.h1>
            <motion.p variants={fadeUp} className="text-white/70 text-lg max-w-xl leading-relaxed">
              Questions about financing? Ready to start an application? Our team responds within 2 business hours.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 lg:px-6 -mt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                icon: Phone,
                title: 'Sales Line',
                value: '+1-866-441-0513',
                href: 'tel:+18664410513',
                sub: 'Mon–Fri 8 AM – 6 PM',
                line: 'sales' as const,
              },
              {
                icon: Phone,
                title: 'Service & Parts',
                value: '+1-833-400-2188',
                href: 'tel:+18334002188',
                sub: 'Mon–Fri 8 AM – 6 PM',
                line: 'service' as const,
              },
              {
                icon: Mail,
                title: 'Email Us',
                value: 'info@truckmaxequipmentfinance.com',
                href: 'mailto:info@truckmaxequipmentfinance.com',
                sub: 'We reply within 2 hours',
                line: undefined,
              },
            ].map((card) => (
              <a
                key={card.title}
                href={card.href}
                onClick={() => { if (card.line) trackEvent('phone_click', { line: card.line }); }}
                className="group bg-white border border-gray-200 rounded-sm p-6 shadow-sm hover:shadow-md hover:border-[#1a2f7e]/30 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-[#1a2f7e]/8 rounded-sm flex items-center justify-center shrink-0 group-hover:bg-[#1a2f7e] transition-colors">
                    <card.icon className="w-5 h-5 text-[#1a2f7e] group-hover:text-white transition-colors" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#cc2020] mb-1">{card.title}</p>
                    <p className="font-bold text-[#1a2f36] text-sm truncate group-hover:text-[#1a2f7e] transition-colors">{card.value}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{card.sub}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Map + Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Left — Map + Info */}
            <motion.div key={mounted ? "rv-info-on" : "rv-info-off"} initial={mounted ? "hidden" : false} whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <div className="overflow-hidden rounded-sm border border-gray-200 shadow-sm mb-8" style={{ height: 320 }}>
                <iframe
                  title="Truck Max Equipment Finance location map"
                  src="https://www.google.com/maps?q=Truck+Max+Equipment+Finance+Miami+FL&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              {/* NAP + Hours */}
              <div
                itemScope
                itemType="https://schema.org/LocalBusiness"
                className="bg-[#f5f7fa] border border-gray-200 rounded-sm p-6 space-y-5"
              >
                <meta itemProp="name" content={SITE_NAME} />
                <meta itemProp="telephone" content={PHONE} />
                <meta itemProp="url" content={SITE_URL} />

                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#1a2f7e] shrink-0 mt-0.5" />
                  <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#cc2020] mb-1">Location</p>
                    <p className="font-semibold text-[#1a2f36] text-sm" itemProp="addressRegion">South Florida</p>
                    <p className="text-slate-500 text-sm">Contact us for our address and directions</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#1a2f7e] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-[#cc2020] mb-2">Hours of Operation</p>
                    <div className="space-y-1">
                      {HOURS.map(({ day, hours }) => (
                        <div key={day} className="flex justify-between text-sm gap-6">
                          <span className="text-[#1a2f36] font-semibold whitespace-nowrap">{day}</span>
                          <span className={hours === 'Closed' ? 'text-slate-400' : 'text-slate-600'}>{hours}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#1a2f7e] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-[#cc2020] mb-1">Phone</p>
                    <a href="tel:+18664410513" className="font-bold text-[#1a2f36] hover:text-[#cc2020] transition-colors" onClick={() => trackEvent('phone_click', { line: 'sales' })}>
                      +1-866-441-0513 <span className="text-slate-400 font-normal text-xs">(Sales)</span>
                    </a>
                    <br />
                    <a href="tel:+18334002188" className="font-bold text-[#1a2f36] hover:text-[#cc2020] transition-colors" onClick={() => trackEvent('phone_click', { line: 'service' })}>
                      +1-833-400-2188 <span className="text-slate-400 font-normal text-xs">(Service)</span>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right — Contact Form */}
            <motion.div initial={mounted ? "hidden" : false} whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ transitionDelay: '120ms' }}>
              <div className="bg-white border border-gray-200 rounded-sm p-8 shadow-sm">
                <h2 className="text-2xl font-extrabold text-[#1a2f36] mb-2 tracking-tight">Send Us a Message</h2>
                <p className="text-slate-500 text-sm mb-7">Our financing team responds within 2 business hours.</p>

                {isSuccess ? (
                  <div className="flex flex-col items-center text-center py-12 gap-4">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-[#1a2f36]">Message Sent!</h3>
                    <p className="text-slate-500 text-sm max-w-xs">
                      We've received your message and will be in touch within 2 business hours.
                    </p>
                  </div>
                ) : (
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-[#1a2f36]/80 font-semibold text-sm">Full Name</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="John Smith" className="bg-white border-gray-300 h-12 focus-visible:ring-[#1a2f7e]" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-[#1a2f36]/80 font-semibold text-sm">Phone Number</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="+1 (305) 555-0100" className="bg-white border-gray-300 h-12 focus-visible:ring-[#1a2f7e]" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[#1a2f36]/80 font-semibold text-sm">Email Address</FormLabel>
                            <FormControl>
                              <Input {...field} type="email" placeholder="you@company.com" className="bg-white border-gray-300 h-12 focus-visible:ring-[#1a2f7e]" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[#1a2f36]/80 font-semibold text-sm">Message</FormLabel>
                            <FormControl>
                              <Textarea
                                {...field}
                                placeholder="Tell us about the vehicle or equipment you'd like to finance, your business, or any questions you have..."
                                className="bg-white border-gray-300 min-h-[130px] focus-visible:ring-[#1a2f7e] resize-none"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {submitError && (
                        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-sm p-3">
                          <AlertCircle className="w-4 h-4 shrink-0" />
                          {submitError}
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="w-full h-13 bg-[#cc2020] hover:bg-[#a81a1a] text-white font-bold rounded-sm uppercase tracking-wide text-sm"
                      >
                        {isLoading ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                        ) : (
                          'Send Message'
                        )}
                      </Button>

                      <p className="text-center text-slate-400 text-xs">
                        Or call us directly: <a href="tel:+18664410513" className="font-semibold text-[#1a2f7e] hover:underline" onClick={() => trackEvent('phone_click', { line: 'sales' })}>+1-866-441-0513</a>
                      </p>
                    </form>
                  </Form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Directions Banner */}
      <section className="py-12 bg-[#1a2f7e]">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-white">
              <p className="font-extrabold text-lg">We serve all of Florida — no in-person visit required.</p>
              <p className="text-white/65 text-sm mt-1">Applications, approvals, and funding all handled remotely. Or stop by our South Florida office by appointment.</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <a href="tel:+18664410513" onClick={() => trackEvent('phone_click', { line: 'sales' })}>
                <Button className="bg-white text-[#1a2f7e] hover:bg-white/90 font-bold rounded-sm uppercase tracking-wide h-12 px-6">
                  <Phone className="w-4 h-4 mr-2" /> Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
